import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Loading from './Loading';
import Post from './Post';
import { trackEventIfConsented } from './analyticsConsent';
import { trackInternalNavigationIfConsented, usePageGroupTracking } from './pageAnalytics';
import { fetchRecentPosts } from './postData';
import {
  SETUP_CATEGORY_DEFINITIONS,
  getCategoryBySlug,
  getRankedPostsForCategory,
  getRelatedCategories,
  getSetupPageData,
  parseListTags,
} from './setupContent';
import { usePageSeo } from './seo';

const FEATURED_EDITORIAL_LINKS = {
  'cable-management': [
    {
      title: 'How to hide cables on a desk',
      to: '/how-to-hide-cables-on-a-desk',
      destination: 'search_hub',
    },
    {
      title: 'Desk cable management ideas',
      to: '/collections/desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Clean desk setup ideas',
      to: '/collections/clean-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'White desk setup ideas',
      to: '/collections/white-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Black desk setup ideas',
      to: '/collections/black-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Standing desk cable management ideas',
      to: '/collections/standing-desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Dual monitor cable management ideas',
      to: '/collections/dual-monitor-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Monitor arm cable management ideas',
      to: '/collections/monitor-arm-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Small desk cable management ideas',
      to: '/collections/small-desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Laptop and monitor cable management ideas',
      to: '/collections/laptop-and-monitor-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Clamshell laptop desk setup ideas',
      to: '/collections/clamshell-laptop-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Small desk clamshell cable management ideas',
      to: '/collections/small-desk-clamshell-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Docking stations for small desks',
      to: '/gear/docking-stations-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'USB-C hubs for small desks',
      to: '/gear/usb-c-hubs-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Under-desk cable management trays',
      to: '/gear/under-desk-cable-management-trays',
      destination: 'gear_page',
    },
    {
      title: 'Cable management boxes for desks',
      to: '/gear/cable-management-boxes-for-desks',
      destination: 'gear_page',
    },
    {
      title: 'Cable sleeves for desk setups',
      to: '/gear/cable-sleeves-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Cable clips for desk setups',
      to: '/gear/cable-clips-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Cable raceways for desks',
      to: '/gear/cable-raceways-for-desks',
      destination: 'gear_page',
    },
    {
      title: 'Power strip mounts for desks',
      to: '/gear/power-strip-mounts-for-desks',
      destination: 'gear_page',
    },
    {
      title: 'Cable management spines for standing desks',
      to: '/gear/cable-management-spines-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting ideas for desk setups',
      to: '/collections/monitor-lighting-for-desk-setups',
      destination: 'collection_page',
    },
    {
      title: 'Monitor arms for clean desk setups',
      to: '/gear/monitor-arms-for-clean-setups',
      destination: 'gear_page',
    },
  ],
  'small-desk': [
    {
      title: 'Desk shelf ideas for small desks',
      to: '/collections/desk-shelf-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Small desk setup ideas',
      to: '/collections/small-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Laptop and monitor setup ideas for small desks',
      to: '/collections/small-desk-setup-ideas-with-laptop-and-monitor',
      destination: 'collection_page',
    },
    {
      title: 'Open laptop desk setup ideas for small desks',
      to: '/collections/open-laptop-desk-setup-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Open laptop cable management ideas for small desks',
      to: '/collections/open-laptop-cable-management-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Open laptop monitor lighting ideas for small desks',
      to: '/collections/open-laptop-monitor-lighting-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Open laptop monitor placement ideas for small desks',
      to: '/collections/open-laptop-monitor-placement-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Open laptop video call setup ideas for small desks',
      to: '/collections/open-laptop-video-call-setup-ideas-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Laptop stands for small desks',
      to: '/gear/laptop-stands-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Clamshell laptop desk setup ideas',
      to: '/collections/clamshell-laptop-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Small desk clamshell cable management ideas',
      to: '/collections/small-desk-clamshell-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Vertical laptop stands for small desks',
      to: '/gear/vertical-laptop-stands-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Docking stations for small desks',
      to: '/gear/docking-stations-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'USB-C hubs for small desks',
      to: '/gear/usb-c-hubs-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves and monitor risers for cleaner setups',
      to: '/gear/desk-shelves-and-monitor-risers',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for small desks',
      to: '/gear/monitor-arms-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for small desks',
      to: '/gear/monitor-risers-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for small desks',
      to: '/gear/monitor-stands-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for clean desk setups',
      to: '/gear/monitor-arms-for-clean-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk setup accessories for cleaner workspaces',
      to: '/gear/desk-setup-accessories-for-cleaner-workspaces',
      destination: 'gear_page',
    },
    {
      title: 'Desk setup accessories for small desks',
      to: '/gear/desk-setup-accessories-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk mats for small desks',
      to: '/gear/desk-mats-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Dual monitor desk setup ideas',
      to: '/collections/dual-monitor-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Monitor lighting ideas for desk setups',
      to: '/collections/monitor-lighting-for-desk-setups',
      destination: 'collection_page',
    },
    {
      title: 'Monitor lighting for small desks',
      to: '/collections/monitor-lighting-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Small desk cable management ideas',
      to: '/collections/small-desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Laptop and monitor cable management ideas',
      to: '/collections/laptop-and-monitor-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Desk lamps for small desks',
      to: '/gear/desk-lamps-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for small desks',
      to: '/gear/monitor-light-bars-for-small-desks',
      destination: 'gear_page',
    },
  ],
  'standing-desk': [
    {
      title: 'Standing desk setup accessories',
      to: '/gear/standing-desk-setup-accessories',
      destination: 'gear_page',
    },
    {
      title: 'Anti-fatigue mats for standing desks',
      to: '/gear/anti-fatigue-mats-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for standing desks',
      to: '/gear/monitor-arms-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for standing desks',
      to: '/gear/monitor-risers-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for standing desks',
      to: '/gear/desk-shelves-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for desk setups',
      to: '/gear/desk-shelves-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for standing desks',
      to: '/gear/monitor-stands-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for standing desks',
      to: '/gear/monitor-light-bars-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for standing desks',
      to: '/gear/desk-lamps-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for desk setups',
      to: '/gear/desk-lamps-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for standing desks',
      to: '/collections/monitor-lighting-for-standing-desks',
      destination: 'collection_page',
    },
    {
      title: 'Standing desk cable management ideas',
      to: '/collections/standing-desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Standing desk setup ideas collection',
      to: '/collections/standing-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Standing desk setup ideas with walking pad',
      to: '/collections/standing-desk-setup-ideas-with-walking-pad',
      destination: 'collection_page',
    },
    {
      title: 'Desk cable management ideas',
      to: '/collections/desk-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Under-desk cable management trays',
      to: '/gear/under-desk-cable-management-trays',
      destination: 'gear_page',
    },
  ],
  'desk-shelves': [
    {
      title: 'Monitor risers for home office setups',
      to: '/gear/monitor-risers-for-home-office-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for home office setups',
      to: '/gear/desk-shelves-for-home-office-setups',
      destination: 'gear_page',
    },
  ],
  'monitor-arms': [
    {
      title: 'Monitor arm desk setup',
      to: '/monitor-arm-desk-setup',
      destination: 'search_hub',
    },
    {
      title: 'Monitor arms for clean desk setups',
      to: '/gear/monitor-arms-for-clean-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for home office setups',
      to: '/gear/monitor-arms-for-home-office-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for home office setups',
      to: '/gear/monitor-stands-for-home-office-setups',
      destination: 'gear_page',
    },
    {
      title: 'Minimalist desk setup ideas',
      to: '/collections/minimalist-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'MacBook desk setup ideas',
      to: '/collections/macbook-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Laptop desk setup ideas',
      to: '/collections/laptop-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'White desk setup ideas',
      to: '/collections/white-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Black desk setup ideas',
      to: '/collections/black-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Monitor stands for desk setups',
      to: '/gear/monitor-stands-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for desk setups',
      to: '/gear/monitor-risers-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for desk setups',
      to: '/gear/desk-shelves-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelf setup ideas',
      to: '/collections/desk-shelf-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Monitor light bar desk setups',
      to: '/collections/monitor-light-bar-desk-setups',
      destination: 'collection_page',
    },
    {
      title: 'Desk lamp setup ideas',
      to: '/collections/desk-lamp-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Productivity desk setup ideas',
      to: '/collections/productivity-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Video call desk setup ideas',
      to: '/collections/video-call-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Video call lighting desk setups',
      to: '/collections/video-call-lighting-desk-setups',
      destination: 'collection_page',
    },
    {
      title: 'Desk lighting for video call setups',
      to: '/gear/desk-lighting-for-video-call-setups',
      destination: 'gear_page',
    },
    {
      title: 'Webcam placement for desk setups',
      to: '/gear/webcam-placement-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for standing desks',
      to: '/gear/monitor-arms-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for standing desks',
      to: '/gear/monitor-risers-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for standing desks',
      to: '/gear/desk-shelves-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for standing desks',
      to: '/gear/monitor-stands-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for standing desks',
      to: '/gear/monitor-light-bars-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for standing desks',
      to: '/gear/desk-lamps-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for standing desks',
      to: '/collections/monitor-lighting-for-standing-desks',
      destination: 'collection_page',
    },
    {
      title: 'Monitor arms for dual monitor setups',
      to: '/gear/monitor-arms-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for dual monitor setups',
      to: '/gear/monitor-risers-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for dual monitor setups',
      to: '/gear/desk-shelves-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for dual monitor setups',
      to: '/gear/monitor-stands-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for dual monitor setups',
      to: '/gear/monitor-light-bars-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for dual monitor setups',
      to: '/gear/desk-lamps-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for dual monitor setups',
      to: '/collections/monitor-lighting-for-dual-monitor-setups',
      destination: 'collection_page',
    },
    {
      title: 'Monitor arms for small desks',
      to: '/gear/monitor-arms-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for small desks',
      to: '/gear/monitor-stands-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor arms for ultrawide setups',
      to: '/gear/monitor-arms-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor stands for ultrawide setups',
      to: '/gear/monitor-stands-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor risers for ultrawide setups',
      to: '/gear/monitor-risers-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk shelves for ultrawide setups',
      to: '/gear/desk-shelves-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for ultrawide setups',
      to: '/gear/monitor-light-bars-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for ultrawide setups',
      to: '/gear/desk-lamps-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for ultrawide setups',
      to: '/collections/monitor-lighting-for-ultrawide-setups',
      destination: 'collection_page',
    },
    {
      title: 'Ultrawide monitor desk setup ideas',
      to: '/collections/ultrawide-monitor-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Ultrawide cable management ideas',
      to: '/collections/ultrawide-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Dual monitor desk setup ideas',
      to: '/collections/dual-monitor-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Dual monitor cable management ideas',
      to: '/collections/dual-monitor-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Monitor arm cable management ideas',
      to: '/collections/monitor-arm-cable-management-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Desk shelves and monitor risers for cleaner setups',
      to: '/gear/desk-shelves-and-monitor-risers',
      destination: 'gear_page',
    },
  ],
  'desk-lighting': [
    {
      title: 'Desk lighting ideas',
      to: '/desk-lighting-ideas',
      destination: 'search_hub',
    },
    {
      title: 'Monitor lighting ideas for desk setups',
      to: '/collections/monitor-lighting-for-desk-setups',
      destination: 'collection_page',
    },
    {
      title: 'Home office desk setup ideas',
      to: '/collections/home-office-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Desk lamps for home office setups',
      to: '/gear/desk-lamps-for-home-office-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for desk setups',
      to: '/gear/desk-lamps-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for dual monitor setups',
      to: '/gear/desk-lamps-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for dual monitor setups',
      to: '/collections/monitor-lighting-for-dual-monitor-setups',
      destination: 'collection_page',
    },
    {
      title: 'Desk lamps for small desks',
      to: '/gear/desk-lamps-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for small desks',
      to: '/gear/monitor-light-bars-for-small-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for desk setups',
      to: '/gear/monitor-light-bars-for-desk-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for standing desks',
      to: '/gear/monitor-light-bars-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for standing desks',
      to: '/gear/desk-lamps-for-standing-desks',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for standing desks',
      to: '/collections/monitor-lighting-for-standing-desks',
      destination: 'collection_page',
    },
    {
      title: 'Monitor light bars for dual monitor setups',
      to: '/gear/monitor-light-bars-for-dual-monitor-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor light bars for ultrawide setups',
      to: '/gear/monitor-light-bars-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Desk lamps for ultrawide setups',
      to: '/gear/desk-lamps-for-ultrawide-setups',
      destination: 'gear_page',
    },
    {
      title: 'Monitor lighting for ultrawide setups',
      to: '/collections/monitor-lighting-for-ultrawide-setups',
      destination: 'collection_page',
    },
    {
      title: 'Monitor lighting for small desks',
      to: '/collections/monitor-lighting-for-small-desks',
      destination: 'collection_page',
    },
    {
      title: 'Small desk setup ideas',
      to: '/collections/small-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Laptop and monitor setup ideas for small desks',
      to: '/collections/small-desk-setup-ideas-with-laptop-and-monitor',
      destination: 'collection_page',
    },
    {
      title: 'Dual monitor desk setup ideas',
      to: '/collections/dual-monitor-desk-setup-ideas',
      destination: 'collection_page',
    },
    {
      title: 'Desk shelves and monitor risers for cleaner setups',
      to: '/gear/desk-shelves-and-monitor-risers',
      destination: 'gear_page',
    },
    {
      title: 'Desk setup accessories for cleaner workspaces',
      to: '/gear/desk-setup-accessories-for-cleaner-workspaces',
      destination: 'gear_page',
    },
  ],
};

const SetupCategoryPage = () => {
  const { categorySlug } = useParams();
  const category = getCategoryBySlug(categorySlug);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  usePageSeo({
    title: category ? category.title : 'Setup ideas',
    description: category ? category.description : 'Browse 101Setups topic hubs and real setup inspiration.',
    canonicalPath: category ? `/setup-ideas/${category.slug}` : '/desk-setup-ideas',
    type: 'website',
    robots: category ? 'index,follow' : 'noindex,nofollow',
  });
  usePageGroupTracking('topic_hub', {
    topic_slug: category?.slug || 'unknown',
  });

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      if (!category) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const recentPosts = await fetchRecentPosts(120);

      if (!isMounted) {
        return;
      }

      setPosts(recentPosts);
      setLoading(false);
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, [category]);

  const rankedPosts = useMemo(() => {
    if (!category) {
      return [];
    }

    return getRankedPostsForCategory(posts, category);
  }, [category, posts]);

  const directMatches = rankedPosts.filter(entry => entry.score > 0);
  const visiblePosts = (directMatches.length > 0 ? directMatches : rankedPosts).slice(0, 12).map(entry => entry.post);
  const relatedCategories = category ? getRelatedCategories(category) : SETUP_CATEGORY_DEFINITIONS.slice(0, 3);
  const featuredEditorialLinks = category ? FEATURED_EDITORIAL_LINKS[category.slug] || [] : [];

  if (loading) {
    return <Loading />;
  }

  if (!category) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Topic hub not found</h1>
          <p>Try the main setup ideas page or browse the latest setups instead.</p>
          <Link className="home-hero-link primary" to="/desk-setup-ideas">
            Go to desk setup ideas
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="page-shell">
      <header className="page-intro">
        <p className="page-kicker">Topic hub</p>
        <h1>{category.title}</h1>
        <p>{category.intro}</p>
        <div className="page-actions">
          <Link
            className="home-hero-link primary"
            to="/latest"
            onClick={() => {
              trackEventIfConsented('hero_cta_click', {
                cta_name: 'browse_latest_setups',
                page_context: category?.slug || 'topic_hub',
              });
              trackInternalNavigationIfConsented({
                link_target: '/latest',
                link_context: 'topic_hub_primary_cta',
                source_page_group: 'topic_hub',
                destination_page_group: 'latest_page',
              });
            }}
          >
            Browse latest setups
          </Link>
          <a
            className="home-hero-link"
            href="/#newsletter"
            onClick={() => {
              trackEventIfConsented('hero_cta_click', {
                cta_name: 'join_newsletter',
                page_context: category?.slug || 'topic_hub',
              });
              trackInternalNavigationIfConsented({
                link_target: '/#newsletter',
                link_context: 'topic_hub_secondary_cta',
                source_page_group: 'topic_hub',
                destination_page_group: 'footer_newsletter',
              });
            }}
          >
            Join the newsletter
          </a>
        </div>
      </header>

      {directMatches.length === 0 ? (
        <div className="editorial-note page-section">
          This hub is live as part of the new search architecture, but the current tag inventory is still thin here. For
          now it falls back to the newest relevant setups while the next content pass enriches titles, stories, and
          category coverage.
        </div>
      ) : null}

      <div className="post-list-grid page-section">
        {visiblePosts.map(model => {
          const setup = getSetupPageData(model);

          return (
            <Post
              key={model.id}
              id={model.id}
              userID={model.userid}
              imgURL={model.imgURL}
              listTags={parseListTags(model.listtags)}
              title={setup.title}
              summary={setup.excerpt}
              categoryLabel={setup.primaryCategory?.shortLabel || category.shortLabel}
              canonicalPath={setup.path}
              sharePath={setup.path}
              altText={setup.altText}
              pageContext="topic_hub"
            />
          );
        })}
      </div>

      <div className="content-grid-two page-section">
        <article className="content-card">
          <h2>Why this hub exists</h2>
          <p>{category.description}</p>
        </article>
        <article className="content-card">
          <h2>Explore next</h2>
          <ul className="page-summary-list">
            {featuredEditorialLinks.map(link => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  onClick={() =>
                    trackInternalNavigationIfConsented({
                      link_target: link.to,
                      link_context: 'topic_hub_featured_editorial',
                      source_page_group: 'topic_hub',
                      destination_page_group: link.destination,
                    })
                  }
                >
                  {link.title}
                </Link>
              </li>
            ))}
            {relatedCategories.map(related => (
              <li key={related.slug}>
                <Link
                  to={`/setup-ideas/${related.slug}`}
                  onClick={() =>
                    trackInternalNavigationIfConsented({
                      link_target: `/setup-ideas/${related.slug}`,
                      link_context: 'topic_hub_related_topic',
                      source_page_group: 'topic_hub',
                      destination_page_group: 'topic_hub',
                    })
                  }
                >
                  {related.title}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/latest"
                onClick={() =>
                  trackInternalNavigationIfConsented({
                    link_target: '/latest',
                    link_context: 'topic_hub_related_latest',
                    source_page_group: 'topic_hub',
                    destination_page_group: 'latest_page',
                  })
                }
              >
                Latest desk setups
              </Link>
            </li>
          </ul>
        </article>
      </div>
    </section>
  );
};

export default SetupCategoryPage;
